// MUI Imports
import {
  Box,
  Button,
  Grid,
  Backdrop,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Divider,
  IconButton,
} from "@mui/material";
import Tab from "@mui/material/Tab";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import { useTheme, styled } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Icon Imports
import Icon from "../../components/icon";
import AddIcon from "@mui/icons-material/Add";
import HandymanOutlinedIcon from "@mui/icons-material/HandymanOutlined";

// ** Custom Component Imports
import CustomAvatar from "components/avatar";
import useMediaQuery from "@mui/material/useMediaQuery";

// ** Component Imports
import AddAssetDialog from "./component/AddAsset";
import AddAssetTypeDialog from "./component/AddAssetType";
import AddMaterialDialog from "./component/AddMaterial";
import AddMaterialTypeDialog from "./component/AddMaterialType";

// ** Logic Imports
import { useIndexLogic } from "./logic/index-logic";
import { useInventoryPermission } from "./permissions/InventoryPermissions";

// Layout Imports
import UserLayout from "../../components/layouts/UserLayout";
import ImportExportOptions from "components/ImportExportOptions";

const Inventory = () => {
  //#region Permissions
  const { InventoryPermissionProps } = useInventoryPermission();
  const {
    hasAssetSavePermission,
    hasMaterialSavePermission,
    hasTypeCatalogSavePermission,
  } = InventoryPermissionProps;
  //#endregion Permissions

  const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { IndexProps } = useIndexLogic();
  const {
    contentHeightFixed,
    loading,
    setLoading,
    collapse,
    setCollapse,
    tabValue,
    tabTitle,
    handleTabChange,
    
    // Asset states
    assetIsEmpty,
    assets,
    assetTypes,
    openAssetDlg,
    openAssetTypeDlg,
    selectedAsset,
    selectedAssetType,
    
    // Material states
    materialIsEmpty,
    materials,
    materialTypes,
    openMaterialDlg,
    openMaterialTypeDlg,
    selectedMaterial,
    selectedMaterialType,
    
    // Column definitions
    columnsAsset,
    columnsAssetType,
    columnsMaterial,
    columnsMaterialType,
    
    // Asset handlers
    handleOpenAssetDlg,
    handleCloseAssetDlg,
    handleSaveAsset,
    handleOpenAssetTypeDlg,
    handleCloseAssetTypeDlg,
    
    // Material handlers
    handleOpenMaterialDlg,
    handleCloseMaterialDlg,
    handleSaveMaterial,
    handleOpenMaterialTypeDlg,
    handleCloseMaterialTypeDlg,
  } = IndexProps;

  return (
    <UserLayout
      contentHeightFixed={contentHeightFixed}
      activeLink={"Inventory"}
    >
      <Card sx={{ width: "100%", height: "100%", p: { xs: 1, sm: 2 } }}>
        <CardContent
          sx={{
            p: { xs: 0, sm: 2 },

            "&:last-child": { pb: { xs: 0, sm: 2 } },
          }}
        >
          {!loading && (
           <TabContext value={tabValue}>
  <Box sx={{ width: "100%" }}>
    {/* Horizontal TabList at top */}
    <TabList
      orientation="horizontal"
      onChange={handleTabChange}
      sx={{
        borderBottom: 1,
        justifyContent: "center",
        borderColor: "divider",
        mb: 2, // space below tabs
        "& .MuiTab-root": {
          minHeight: 40,
          px: 3,
          textTransform: "none",
        },
      }}
    >
      <Tab
        value="1"
        label={
          <Box sx={{ display: "flex", alignItems: "center", gap: 2,ml:2 }}>
            <Icon icon="carbon:financial-assets" />
            Assets
          </Box>
        }
      />
      <Tab
        value="2"
        label={
          <Box sx={{ display: "flex", alignItems: "center", gap: 2,ml:3 }}>
            <Icon icon="lucide:boxes" />
            Materials
          </Box>
        }
      />
    </TabList>
                 {/* {!isMobile && (
                <Box>
                  <IconButton
                    sx={{
                      position: "absolute",
                      left: collapse ? -14 : 124,
                      top: 8,
                      backgroundColor: "#a3e2b9", // light green
                      border: `1px solid ${theme.palette.divider}`,
                      zIndex: 2,
                      width: "30px", // set a fixed width
                      height: "30px",
                      color: "primary.main", // optional: icon color
                      "&:hover": {
                        backgroundColor: "#a3e2b9", // slightly darker on hover
                      },
                    }}
                    size="small"
                    onClick={() => setCollapse(!collapse)}
                  >
                    <Icon
                      icon={
                        collapse
                          ? "tabler:chevron-right"
                          : "tabler:chevron-left"
                      }
                    />
                  </IconButton>
                </Box>
                )} */}
                <TabPanel value="1" sx={{ width: "100%" }}>
                  <Grid
                    container
                    spacing={{ xs: 2, sm: 10 }}
                    sx={{ width: "100%" }}
                  >
                    {assetIsEmpty ? (
  <Grid item xs={12}>
    <Card sx={{ my: 6 }}>
      <CardContent
        sx={{
          display: "flex",
          textAlign: "center",
          alignItems: "center",
          justifyContent: "center", // Add this to center horizontally
          flexDirection: "column", // Always use column layout
          backgroundColor: "#FBFBFC",
          p: (theme) =>
            `${theme.spacing(9.75, 5, 9.25)} !important`,
        }}
      >
        <CustomAvatar
          skin="light"
          color="info"
          sx={{ mb: 2.25 }}
        >
          <HandymanOutlinedIcon />
        </CustomAvatar>
        <Typography variant="h5" sx={{ mb: 2.75 }}>
          No Assets added yet
        </Typography>
        <Typography sx={{ mb: 6, color: "text.secondary" }}>
          Add Assets that are required by clicking on
          "Create Asset" button
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          disabled={!hasAssetSavePermission}
          {...(hasAssetSavePermission && {
            onClick: () => handleOpenAssetDlg(),
          })}
        >
          Create Asset
        </Button>
      </CardContent>
    </Card>
  </Grid>
)  : (
                      <>
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", lg: "row" },
                              justifyContent: "space-between",
                              alignItems: { xs: "flex-start", lg: "center" },
                                 flexWrap: "wrap",   
                              gap: { xs: 2, lg: 0 },
                            }}
                          >
                            <Box>
                              <Typography variant="h3">Assets List</Typography>
                              <Typography color="secondary">
                                Vehicles, tools, and equipment that Employees
                                use to complete Jobs
                              </Typography>
                            </Box>
                            <Box  sx={{
                             mt: { xs: 1, lg: 0 },
                            display: "flex",
                             flexWrap: "wrap",            
                                gap: 1,                     
                              }}>
                              <Button
                                variant="contained"
                                color="main"
                                startIcon={<AddIcon />}
                                disabled={!hasAssetSavePermission}
                                {...(hasAssetSavePermission && {
                                  onClick: () => handleOpenAssetDlg(),
                                })}
                                sx={{ width: { xs: "100%", lg: "auto" } }}
                              >
                                New Asset
                              </Button>
                              <ImportExportOptions 
                                handleOpenBulkImportDlg={()=>{}}                          
                                data={assets}
                                module="Assets"
                              />
                            </Box>                            
                          </Box>
                          <Divider sx={{ py: 3 }} />
                        <Box sx={{ height: 400, width: "100%" }}>
                       <DataGridPro
                          key={collapse ? "collapsed" : "expanded"}
                            rows={assets}
                            columns={columnsAsset}
                            disableRowSelectionOnClick
                          //  pagination
                          //  pageSizeOptions={[10, 20, 50]} 
                          //  initialState={{
                          //    pagination: { paginationModel: { pageSize: 10 } },
                          //    }}
                          slots={{
                            footer: () => null
                          }}
                            sx={{
                             "& .MuiDataGrid-columnHeaderTitle": {
                              textTransform: "none",
                              },
                             }}
                             />
                               </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", lg: "row" },
                              justifyContent: "space-between",
                              alignItems: { xs: "flex-start", lg: "center" },
                              gap: { xs: 2, lg: 0 },
                            }}
                          >
                            <Box>
                              <Typography variant="h3">Asset Type</Typography>
                              <Typography color="secondary">
                                Categorize your Assets by Type, like supplies,
                                tools, vehicles, etc.
                              </Typography>
                            </Box>
                            <Box sx={{ mt: { xs: 1, lg: 0 } }}>
                              <Button
                                variant="contained"
                                color="main"
                                startIcon={<AddIcon />}
                                disabled={!hasTypeCatalogSavePermission}
                                {...(hasTypeCatalogSavePermission && {
                                  onClick: () => handleOpenAssetTypeDlg(),
                                })}
                              >
                                New Asset Type
                              </Button>
                            </Box>
                          </Box>
                          <Divider sx={{ py: 3 }} />
                          <Box sx={{ height: 400, width: "100%", overflowX: "auto" }}>
                            <DataGridPro
                              key={collapse ? "collapsed" : "expanded"}
                              rows={assetTypes}
                              columns={columnsAssetType}
                              disableRowSelectionOnClick
                            //   pagination
                            //   pageSizeOptions={[10, 20, 50]} 
                            //   initialState={{
                            //   pagination: { paginationModel: { pageSize: 10 } }, 
                            //  }}
                              slots={{
                                footer: () => null
                              }}
                              sx={{
                                minWidth: "600px", // 👈 adjust based on your column count
                                "& .MuiDataGrid-columnHeaderTitle": {
                                  textTransform: "none",
                                },
                              }}
                            />
                          </Box>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </TabPanel>
                <TabPanel value="2" sx={{ width: "100%" }}>
                  <Grid
                    container
                    spacing={{ xs: 2, sm: 5 }}
                    sx={{ width: "100%" }}
                  >
                    {materialIsEmpty ? (
                      <Grid item xs={12}>
                        <Card sx={{ my: 6 }}>
                          <CardContent
                            sx={{
                              display: "flex",
                              textAlign: "center",
                              alignItems: "center",
                              flexDirection: "column",
                              backgroundColor: "#FBFBFC",
                              p: (theme) =>
                                `${theme.spacing(9.75, 5, 9.25)} !important`,
                            }}
                          >
                            <CustomAvatar
                              skin="light"
                              color="info"
                              sx={{ mb: 2.25 }}
                            >
                              <HandymanOutlinedIcon />
                            </CustomAvatar>
                            <Typography variant="h5" sx={{ mb: 2.75 }}>
                              No Materials added yet
                            </Typography>
                            <Typography sx={{ mb: 6, color: "text.secondary" }}>
                              Add Materials that are required by clicking on
                              "Create Material" button
                            </Typography>
                            <Button
                              variant="outlined"
                              color="primary"
                              startIcon={<AddIcon />}
                              disabled={!hasMaterialSavePermission}
                              {...(hasMaterialSavePermission && {
                                onClick: () => handleOpenMaterialDlg(),
                              })}
                            >
                              Create Material
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    ) : (
                      <>
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", lg: "row" },
                              justifyContent: "space-between",
                              alignItems: { xs: "flex-start", lg: "center" },
                              gap: { xs: 2, lg: 0 },
                            }}
                          >
                            <Box>
                              <Typography variant="h3">
                                Materials List
                              </Typography>
                              <Typography color="secondary">
                                Items and supplies that are used in jobs
                              </Typography>
                            </Box>
                            <Box>
                              <Button
                                variant="contained"
                                color="main"
                                startIcon={<AddIcon />}
                                disabled={!hasMaterialSavePermission}
                                {...(hasMaterialSavePermission && {
                                  onClick: () => handleOpenMaterialDlg(),
                                })}
                              >
                                New Material
                              </Button>
                              <ImportExportOptions 
                              handleOpenBulkImportDlg={()=>{}}                          
                              data={materials}
                              module="Materials"
                            />
                            </Box>                           
                          </Box>
                          <Divider sx={{ py: 3 }} />
                          <Box sx={{ height: 400, width: "100%" }}>
                          <DataGridPro
                            key={collapse ? "collapsed" : "expanded"}
                            //  pagination
                            //  pageSizeOptions={[10, 20, 50]} 
                            //  initialState={{
                            // pagination: { paginationModel: { pageSize: 10 } }, 
                            // }}
                            slots={{
                              footer: () => null
                            }}
                            rows={materials}
                            columns={columnsMaterial}
                            disableRowSelectionOnClick
                            sx={{
                              "& .MuiDataGrid-columnHeaderTitle": {
                                textTransform: "none",
                              },
                            }}
                          />
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", lg: "row" },
                              justifyContent: "space-between",
                              alignItems: { xs: "flex-start", lg: "center" },
                              gap: { xs: 2, lg: 0 },
                            }}
                          >
                            <Box>
                              <Typography variant="h3">
                                Material Type
                              </Typography>
                              <Typography color="secondary">
                                Categorize your Materials by Type, like wood,
                                metal, paint, etc.
                              </Typography>
                            </Box>
                            <Box>
                              <Button
                                variant="contained"
                                color="main"
                                startIcon={<AddIcon />}
                                disabled={!hasTypeCatalogSavePermission}
                                {...(hasTypeCatalogSavePermission && {
                                  onClick: () => handleOpenMaterialTypeDlg(),
                                })}
                              >
                                New Material Type
                              </Button>
                            </Box>
                          </Box>
                          <Divider sx={{ py: 3 }} />
                          <Box sx={{ height: 400, width: "100%", overflowX: "auto" }}>
                            <DataGridPro
                              key={collapse ? "collapsed" : "expanded"}
                            //   pagination
                            //  pageSizeOptions={[10, 20, 50]} 
                            //  initialState={{
                            // pagination: { paginationModel: { pageSize: 10 } }, 
                            // }}
                            slots={{
                              footer: () => null
                            }}
                              rows={materialTypes}
                              columns={columnsMaterialType}
                              disableRowSelectionOnClick
                              sx={{
                                minWidth: "600px",
                                "& .MuiDataGrid-columnHeaderTitle": {
                                  textTransform: "none",
                                },
                              }}
                            />
                          </Box>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </TabPanel>
              </Box>
            </TabContext>
          )}
          {loading && (
            <Backdrop
              sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={loading}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
          )}
        </CardContent>
      </Card>
      <AddAssetDialog
        open={openAssetDlg}
        handleClose={handleCloseAssetDlg}
        handleSave={handleSaveAsset}
        setLoading={setLoading}
        selectedAsset={selectedAsset}
        onOpenAssetType={handleOpenAssetTypeDlg}
        assetTypes={assetTypes}
      />
      <AddAssetTypeDialog
        open={openAssetTypeDlg}
        handleClose={handleCloseAssetTypeDlg}
        setLoading={setLoading}
        selectedAssetType={selectedAssetType}
      />
      <AddMaterialDialog
        open={openMaterialDlg}
        handleClose={handleCloseMaterialDlg}
        handleSave={handleSaveMaterial}
        setLoading={setLoading}
        selectedMaterial={selectedMaterial}
        materialTypes={materialTypes}
        onOpenMaterialType={handleOpenMaterialTypeDlg}
      />
      <AddMaterialTypeDialog
        open={openMaterialTypeDlg}
        handleClose={handleCloseMaterialTypeDlg}
        setLoading={setLoading}
        selectedMaterialType={selectedMaterialType}
      />
      {/*<ToastContainer />*/}
    </UserLayout>
  );
};

export default Inventory;
